import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export { useScrollReveal } from "./useScrollReveal";

/**
 * Subscribe to a CSS media query.
 *
 * Used for behaviour that genuinely cannot be expressed in CSS — for example,
 * only attaching the cursor-following project preview on devices that have a
 * real pointer. Layout itself is handled in stylesheets, not here.
 *
 * @param {string} mediaQuery A media query string, e.g. "(min-width: 64rem)".
 * @returns {boolean} Whether the query currently matches.
 */
export function useMediaQuery(mediaQuery) {
  const getMatches = useCallback(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(mediaQuery).matches;
  }, [mediaQuery]);

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQueryList = window.matchMedia(mediaQuery);
    const handleChange = (event) => setMatches(event.matches);

    // Re-sync in case the viewport changed between render and effect.
    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, [mediaQuery]);

  return matches;
}

/** True when the visitor has asked their system to minimise animation. */
export function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

/** True on devices with a precise pointer — excludes phones and tablets. */
export function useHasFinePointer() {
  return useMediaQuery("(hover: hover) and (pointer: fine)");
}

/**
 * Whether it is reasonable to download the hero film right now.
 *
 * This used to also require a tablet-sized viewport, which meant phones never
 * saw the film at all. That gate was calibrated when the file was 28 MB, where
 * refusing to push it over cellular was clearly right. The film is now a
 * 1.9 MB WebM, so the viewport test no longer earns its keep — it was just
 * denying phone visitors the main thing the page is built around.
 *
 * What remains are the tests that still matter:
 *
 *   - the visitor has not asked to reduce motion;
 *   - the connection is not metered (`saveData`) or slow (Network Information
 *     API, where supported — absent support is treated as "fine", since that
 *     is the case on desktop Safari and Firefox).
 *
 * @returns {boolean}
 */
export function useAllowsHeavyMedia() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isConnectionGenerous, setIsConnectionGenerous] = useState(true);

  useEffect(() => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (!connection) return undefined;

    const SLOW_CONNECTION_TYPES = ["slow-2g", "2g", "3g"];
    const evaluateConnection = () => {
      setIsConnectionGenerous(
        !connection.saveData &&
          !SLOW_CONNECTION_TYPES.includes(connection.effectiveType)
      );
    };

    evaluateConnection();
    connection.addEventListener?.("change", evaluateConnection);
    return () => connection.removeEventListener?.("change", evaluateConnection);
  }, []);

  return !prefersReducedMotion && isConnectionGenerous;
}

/**
 * Whether an element is close enough to the viewport to be worth loading for.
 *
 * Used by `LiveCover` to decide when a project's preview loop should exist at
 * all. The archive and the home page carry several of these, and a page that
 * eagerly fetched every one of them would download megabytes the visitor may
 * never scroll to — so the <video> is not mounted until its card approaches,
 * and is thrown away again once it leaves. That also keeps the number of live
 * video decoders down, which is a real limit on phones.
 *
 * `margin` is deliberately generous: starting the download a few hundred
 * pixels early is what makes the loop already be running by the time the card
 * is actually looked at.
 *
 * Where IntersectionObserver is missing the answer is a flat yes, which
 * degrades to "behaves like a normal autoplaying loop" rather than to nothing.
 *
 * @param {React.RefObject<Element>} elementRef
 * @param {string} [margin="250px"] rootMargin for the observer.
 * @returns {boolean}
 */
export function useIsNearViewport(elementRef, margin = "250px") {
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setIsNear(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      { rootMargin: margin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, margin]);

  return isNear;
}

/**
 * Report whether the page has been scrolled past a threshold.
 *
 * The header uses this to swap from its transparent, over-image state to its
 * opaque, hairline-bottomed state. Reads are throttled to one per animation
 * frame so a fast scroll cannot queue up layout work.
 *
 * @param {number} [thresholdInPixels=24]
 * @returns {boolean}
 */
export function useScrolledPast(thresholdInPixels = 24) {
  const [hasScrolledPast, setHasScrolledPast] = useState(false);

  useEffect(() => {
    let animationFrameId = null;

    const evaluate = () => {
      animationFrameId = null;
      setHasScrolledPast(window.scrollY > thresholdInPixels);
    };

    const handleScroll = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [thresholdInPixels]);

  return hasScrolledPast;
}

/**
 * Freeze background scrolling while a modal surface is open.
 *
 * Locking `overflow` alone makes the page jump sideways as the scrollbar
 * disappears, so the width the scrollbar occupied is replaced with padding.
 * The previous inline values are restored on unlock, which keeps nested or
 * rapidly toggled overlays from leaving the page stuck.
 *
 * @param {boolean} isLocked
 * @returns {void}
 */
export function useLockBodyScroll(isLocked) {
  useLayoutEffect(() => {
    if (!isLocked) return undefined;

    const { body } = document;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    body.setAttribute("data-scroll-locked", "true");

    return () => {
      body.removeAttribute("data-scroll-locked");
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isLocked]);
}

/**
 * Run a callback when Escape is pressed, while `isActive` is true.
 *
 * @param {boolean} isActive
 * @param {() => void} onEscape
 * @returns {void}
 */
export function useEscapeKey(isActive, onEscape) {
  // Held in a ref so a new inline callback each render does not re-bind the
  // listener on every parent re-render.
  const callbackRef = useRef(onEscape);
  callbackRef.current = onEscape;

  useEffect(() => {
    if (!isActive) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        callbackRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);
}

/**
 * Keep keyboard focus inside a container while it is open, and hand focus
 * back to whatever was focused before when it closes.
 *
 * Required for the lightbox and the mobile menu: without it, tabbing walks
 * out of the overlay and into the page hidden behind it.
 *
 * @param {boolean} isActive
 * @returns {React.RefObject<HTMLElement>} Attach to the overlay container.
 */
export function useFocusTrap(isActive) {
  const containerRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    previouslyFocusedElementRef.current = document.activeElement;

    const FOCUSABLE_SELECTOR = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "textarea:not([disabled])",
      "select:not([disabled])",
      'iframe',
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    /** @returns {HTMLElement[]} */
    const collectFocusableElements = () =>
      Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null || element.tagName === "IFRAME"
      );

    // Move focus into the overlay so screen readers and keyboards follow.
    const initialTargets = collectFocusableElements();
    (initialTargets[0] ?? container).focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key !== "Tab") return;

      const focusableElements = collectFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Wrap around at both ends of the list.
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
      const elementToRestore = previouslyFocusedElementRef.current;
      if (elementToRestore instanceof HTMLElement) {
        elementToRestore.focus({ preventScroll: true });
      }
    };
  }, [isActive]);

  return containerRef;
}
